<template>
  <!-- Overlay drawer on the end side: host-controlled via v-model.
       P16: NO scrim — the ticket grid stays fully visible; the transparent
       layer only catches click-away. -->
  <Teleport to="body">
    <Transition name="kitchen-drawer-slide">
      <div
        v-if="modelValue"
        class="fixed inset-0"
        @click.self="emit('update:modelValue', false)"
      >
        <div
          ref="drawerRef"
          class="absolute inset-y-0 right-0 flex w-[304px] max-w-full flex-col border-l border-default bg-default"
        >
      <div class="flex h-16 shrink-0 items-center">
        <div class="ml-4 flex min-w-0 flex-1 items-center">
          <!-- P16 header: user name, 800. -->
          <div v-if="store.user" class="truncate font-extrabold">
            {{ store.user?.name }}
          </div>

          <div v-else-if="store.tenant" class="min-w-0">
            <div class="truncate font-extrabold">{{ store.tenant?.name }}</div>
            <div class="text-helper text-muted">
              {{ $t('user_roles.default.super_admin') }}
            </div>
          </div>
        </div>

        <UButton
          v-if="menuOptions.length"
          class="mr-2"
          :ui="{ leadingIcon: 'size-5' }"
          variant="ghost"
          color="neutral"
          icon="i-lucide-ellipsis-vertical"
          @click="openMenu($event)"
        />

        <cod-context-menu
          v-if="menuOptions.length"
          v-model="showItemMenu"
          :options="menuOptions"
          :event="menuEvent || null"
          @select="trigerMenuAction($event)"
        />
      </div>

      <div class="flex-1 overflow-y-auto p-3">
        <template v-if="!showAllCounts">
          <!-- last:border-b keeps the divider under the single item —
               the sections below continue the same list. -->
          <UAccordion
            v-if="!limited"
            v-model="panel"
            type="multiple"
            :items="accordionItems"
            :ui="{ label: 'font-bold', item: 'last:border-b' }"
          >
            <template #body>
              <all-day-items
                :all-items="allItemsAndCategories"
                :selected-items="selectedItems"
                :limited="limited"
                @show-all-counts="onShowAllCounts"
                @update-items="onItemsUpdate"
              />
            </template>
          </UAccordion>

          <!-- P16 settings sections sit directly in the drawer — no
               "Podešavanja" wrapper accordion (owner 2026-07-10). -->
          <kitchen-settings :limited="limited" />
        </template>

        <div
          v-else
          class="p-2"
        >
          <!-- Same geometry as the accordion triggers. -->
          <button
            type="button"
            class="flex w-full items-center gap-1.5 border-b border-default py-3.5 text-left"
            @click="showAllCounts = false"
          >
            <UIcon
              name="i-lucide-arrow-up"
              class="size-5 shrink-0"
            />

            <span class="flex-1 truncate font-bold">{{ $t('kds.drawer.all_day') }}</span>
          </button>

          <all-day-items
            :all-items="allItemsAndCategories"
            :selected-items="selectedItems"
            :height="totalCountListHeight"
            @update-items="onItemsUpdate"
          />
        </div>
      </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, Ref, computed, watch } from 'vue';
import { useAppStore } from '@/stores/useAppStore';
import { AppService, TenantService, UserService } from 'app-core'
import { useI18n } from 'vue-i18n';
import CodContextMenu from '@/components/CodContextMenu.vue';

import { useRouter } from 'vue-router';
import useKitchenTickets from '@/composables/useKitchenTickets';
import useKitchenTicketItems from '@/composables/useKitchenTicketItems';

import KitchenSettings from './KitchenSettings.vue';
import AllDayItems from './AllDayItems.vue';

import { Category, Item } from '@/composables/useKitchenTicketItems';
import { useToast } from '@/composables/useToast';

interface MenuOption {
  action: string;
}

const store = useAppStore();
const router = useRouter();
const { toast } = useToast()

const { selectedItems } = useKitchenTickets();
const { allItems, allItemsAndCategories } = useKitchenTicketItems();
const { t } = useI18n();

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  limited: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:modelValue'])

const drawerRef: Ref<HTMLElement | null> = ref(null);
const showItemMenu = ref(false)
const menuEvent: Ref<PointerEvent | undefined> = ref()
const showAllCounts = ref(false);
const panel = ref(['first']);

const isOnline = computed(() => store.isOnline);

const accordionItems = computed(() => [{
  value: 'first',
  label: t('kds.drawer.all_day'),
  icon: 'i-lucide-arrow-down',
}]);

const menuOptions = computed(() => {
  return [
    {
      isEnabled: !store.terminal?.isKitchenDisplaySystem() && !disableSales.value && UserService.allows('nav_access_tables') && store.user,
      text: t('general.table_plan'),
      data: { action: 'sales' },
      icon: 'i-lucide-redo-2',
    },
    {
      isEnabled: !store.terminal?.isKitchenDisplaySystem() && UserService.allows('nav_access_admin'),
      text: t('general.administration'),
      data: { action: 'admin' },
      icon: 'i-lucide-settings'
    },
    {
      isEnabled: !store.terminal?.isKitchenDisplaySystem() && !!store.user,
      text: t('general.log_out'),
      data: { action: 'logout' },
      icon: 'i-lucide-log-out'
    },
    {
      isEnabled: store.terminal?.isKitchenDisplaySystem() || !store.user,
      text: t('general.manager_logout'),
      data: { action: 'logoutTenant' },
      icon: 'i-lucide-log-out'
    }].filter(item => item.isEnabled)
});

const totalListHeight = computed(() => {
  const top = 0;
  const gutters = 64;
  const drawerHeight = drawerRef.value ? drawerRef.value.clientHeight : 0;

  return drawerHeight - top - gutters;
});

const disableSales = computed(() => {
  const singleCashRegister = store.tenant?.venue && !!store.tenant.venue.settings.cash_register.single
  return !singleCashRegister && !store.impersonate && store.device?.platform === 'web' && import.meta.env.VITE_ENV === 'production'
})

const totalCountListHeight = computed(() => {
  return totalListHeight.value - 88;
});

watch(() => props.modelValue, value => {
  if (value) {
    showAllCounts.value = false;
  }
});

watch(() => allItems.value, value => {
  const items = value.map((item: Category) => item.id);
  selectedItems.value = selectedItems.value.filter((item: Item) => items.includes(item.id));
});

function openMenu(event: MouseEvent) {
  event.stopPropagation()
  event.preventDefault()

  menuEvent.value = event as PointerEvent
  showItemMenu.value = true
}

const onShowAllCounts = () => {
  showAllCounts.value = true;
};

const onItemsUpdate = (item: Item) => {
  if (selectedItems.value.map((selectedItem) => selectedItem.id).includes(item.id)) {
    selectedItems.value = selectedItems.value.filter((selectedItem) => selectedItem.id !== item.id);
  } else {
    selectedItems.value.push(item);
  }
};

const trigerMenuAction = (event: MenuOption) => {
  if (event.action === 'sales') router.push({ name: 'sales' });
  if (event.action === 'admin') AppService.goToAdministration();
  if (event.action === 'logout') logout();
  if (event.action === 'logoutTenant') logoutTenant();
}

const logoutTenant = async () => {
  if (!isOnline.value) {
    toast(t('connection.internet_offline_message_not_allowed'), 'error');
    return;
  }

  await TenantService.logout();
  router.push({ name: 'tenantLogin' });
};

const logout = () => {
  UserService.logout();
  router.push({ name: 'userLogin' });
};
</script>

<style scoped>
.kitchen-drawer-slide-enter-active,
.kitchen-drawer-slide-leave-active {
  transition: opacity 0.2s ease;
}
.kitchen-drawer-slide-enter-active > div,
.kitchen-drawer-slide-leave-active > div {
  transition: transform 0.2s ease;
}
.kitchen-drawer-slide-enter-from,
.kitchen-drawer-slide-leave-to {
  opacity: 0;
}
.kitchen-drawer-slide-enter-from > div,
.kitchen-drawer-slide-leave-to > div {
  transform: translateX(100%);
}
</style>
